import type { TankDTO } from "../../shared/protocol/index.js";
import type { Renderer } from "./Renderer.js";

/**
 * Running signed distance (world px) a tank has rolled along its own forward
 * axis, for scrolling tread/wheel graphics in lock-step with real motion —
 * instead of a clock-driven animation that keeps "moving" while the tank sits
 * still or just turns in place. Positive while driving forward, negative in
 * reverse, frozen (no change) while stationary or strafing sideways. A big
 * jump (e.g. a respawn teleport) is treated as a re-plant, not a roll.
 */
export function tankTreadPhase(rd: Renderer, t: TankDTO): number {
  const last = rd.lastTreadPos.get(t.id);
  let phase = rd.treadPhase.get(t.id) ?? 0;
  if (last) {
    const dx = t.x - last.x;
    const dy = t.y - last.y;
    if (Math.hypot(dx, dy) < rd.tankR * 6) {
      phase += dx * Math.cos(t.bodyAngle) + dy * Math.sin(t.bodyAngle);
    }
  }
  rd.lastTreadPos.set(t.id, { x: t.x, y: t.y });
  rd.treadPhase.set(t.id, phase);
  return phase;
}

export function lerp(a: number, b: number, f: number): number {
  return a + (b - a) * f;
}
export function angleLerp(a: number, b: number, f: number): number {
  let d = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * f;
}
export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Squared distance from point (px,py) to segment (ax,ay)-(bx,by). */
export function pointSegDist2(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return (px - cx) ** 2 + (py - cy) ** 2;
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function shade(hex: string, amt: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = clampByte((n >> 16) + amt);
  const g = clampByte(((n >> 8) & 0xff) + amt);
  const b = clampByte((n & 0xff) + amt);
  return `rgb(${r},${g},${b})`;
}
function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/** "#rrggbb" + alpha → an rgba() string. */
export function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff},${alpha})`;
}
